import React, { useEffect, useMemo, useState } from 'react'
import { ButtonGroup, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { AddIcon } from '@reapit/elements/icons/add'
import { toast } from '@reapit/elements/core/toaster'
import { AdminFormField, AdminFormFieldPayload, ConditionSource, EnumOption } from '../../../../api/types'
import { useFieldAuthoring } from '../../../../api/field-authoring'
import {
  FieldHelp,
  FieldStack,
  OptionRemoveWrap,
  OptionRowLayout,
  OptionsBlock,
  SectionHeading,
} from '../../../../components/form-details-fields/style'

interface ConditionRow {
  key: string
  /** Present when this row backs an already-saved dependency (edit); absent for a newly-added row. */
  dependencyRef?: string
  /** The controlling composite field (drives which options are offered below). */
  controllingFieldRef: string
  /** The option of the controlling field that triggers this condition. */
  optionRef: string
}

let conditionKeySeq = 0
const nextConditionKey = () => `collection-condition-${(conditionKeySeq += 1)}`

/** Minimal full payload — only the props a Collection/Group needs are overridden per call. */
const blankFieldPayload: AdminFormFieldPayload = {
  jsonPropertyName: null,
  jsonObjectName: null,
  jsonObjectIsArray: false,
  fieldType: 'None',
  dataType: 'Text',
  validation: 'None',
  displayGroupName: '',
  displayGroupDescription: null,
  displayGroupIndex: 0,
  displayGroupSubIndex: 0,
  displayPrefix: null,
  displaySuffix: null,
  order: 0,
  defaultData: null,
  booleanTrueData: null,
  booleanFalseData: null,
  defaultDataHasMergeField: false,
  editType: 'None',
  editWidth: null,
  editDisplayName: null,
  editDisplayDescription: null,
  editPlaceholder: null,
  editOrder: 0,
  editAllowAddSection: null,
  editAddSectionButtonText: null,
  min: null,
  max: null,
}

const toNumber = (value: string): number | null => {
  const parsed = Number(value)
  return value.trim() === '' || Number.isNaN(parsed) ? null : parsed
}

interface CollectionDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  sectionReferenceID: string
  /** The Collection field being edited, or null to create a new one. */
  collection: AdminFormField | null
  /** Other composite fields whose option can gate this collection's visibility. */
  conditionSources?: ConditionSource[]
  /** This collection's existing visibility conditions (shown when ANY match), each with the dependency row
   *  backing it (for updating/removing that specific condition). Empty = always shown. */
  currentConditions?: { dependencyRef: string; optionRef: string }[]
  /** Group Keys already used by existing plain (object-backed) fields in this section. This collection's own
   *  Item Key must not reuse one of these - it would store as a JSON array under a key a plain field expects
   *  to be an object, colliding the same way a plain field reusing an existing collection's key does. */
  reservedGroupKeys?: string[]
  nextOrder: number
  onSaved: () => void
}

/**
 * Add/Edit drawer for a Collection (a repeatable group). On create it provisions the Collection field, a
 * bare Group field, and the Group→Collection link; children are added afterwards from the canvas. Optionally
 * gates the collection's visibility on a controlling field's option (same mechanism as a field's Visibility).
 */
export const CollectionDrawer: React.FC<CollectionDrawerProps> = ({
  isOpen,
  onOpenChange,
  sectionReferenceID,
  collection,
  conditionSources = [],
  currentConditions = [],
  reservedGroupKeys = [],
  nextOrder,
  onSaved,
}) => {
  const formId = 'collection-form'
  const { createFormField, updateFormField, createDependency, updateDependency, deleteDependency } = useFieldAuthoring()
  const [submitting, setSubmitting] = useState(false)

  const [displayName, setDisplayName] = useState('')
  const [itemKey, setItemKey] = useState('')
  const [addButtonText, setAddButtonText] = useState('')
  const [min, setMin] = useState('')
  const [max, setMax] = useState('')
  const [order, setOrder] = useState('')
  const [error, setError] = useState<string | null>(null)

  /** Visibility conditions: shown when ANY row's controlling field has the row's chosen option (empty =
   *  always shown). */
  const [conditions, setConditions] = useState<ConditionRow[]>([])

  const controllingOptions = useMemo<EnumOption[]>(
    () => conditionSources.map((source) => ({ value: source.fieldReferenceID, label: source.label })),
    [conditionSources],
  )
  const optionChoicesFor = (controllingFieldRef: string): EnumOption[] => {
    const source = conditionSources.find((entry) => entry.fieldReferenceID === controllingFieldRef)
    return (source?.options ?? []).map((option) => ({ value: option.referenceID, label: option.label }))
  }

  useEffect(() => {
    if (!isOpen) return
    setError(null)
    setDisplayName(collection?.editDisplayName ?? '')
    setItemKey(collection?.jsonObjectName ?? '')
    setAddButtonText(collection?.editAddSectionButtonText ?? '')
    setMin(collection?.min != null ? String(collection.min) : '')
    setMax(collection?.max != null ? String(collection.max) : '')
    setOrder(collection?.order != null ? String(collection.order) : String(nextOrder))

    setConditions(
      currentConditions.map((condition) => {
        const source = conditionSources.find((entry) =>
          entry.options.some((option) => option.referenceID === condition.optionRef),
        )
        return {
          key: nextConditionKey(),
          dependencyRef: condition.dependencyRef,
          controllingFieldRef: source?.fieldReferenceID ?? '',
          optionRef: condition.optionRef,
        }
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, collection])

  const addCondition = () =>
    setConditions((prev) => [...prev, { key: nextConditionKey(), controllingFieldRef: '', optionRef: '' }])
  const updateConditionControllingField = (index: number, controllingFieldRef: string) =>
    setConditions((prev) =>
      prev.map((condition, i) => {
        return i === index ? { ...condition, controllingFieldRef, optionRef: '' } : condition
      }),
    )
  const updateConditionOption = (index: number, optionRef: string) =>
    setConditions((prev) =>
      prev.map((condition, i) => {
        return i === index ? { ...condition, optionRef } : condition
      }),
    )
  const removeCondition = (index: number) => setConditions((prev) => prev.filter((_, i) => i !== index))

  const handleClose = () => onOpenChange(false)

  const buildCollectionPayload = (): AdminFormFieldPayload => ({
    ...(collection
      ? (() => {
          const { referenceID, ...rest } = collection
          void referenceID
          return rest
        })()
      : { ...blankFieldPayload }),
    editType: 'Collection',
    editDisplayName: displayName.trim() || null,
    jsonObjectName: itemKey.trim() || null,
    jsonObjectIsArray: false,
    editAllowAddSection: true,
    editAddSectionButtonText: addButtonText.trim() || null,
    min: toNumber(min),
    max: toNumber(max),
    // The Display Order input only ever edits `order` (the review/summary-screen position, which is what the
    // backend's Collection-ordering fallback reads). `editOrder` (the edit-screen position) is a separate,
    // independently-set value with no UI of its own here - tying it to this same input would silently move a
    // Collection in the edit screen whenever this drawer is saved for any reason (e.g. just renaming it).
    editOrder: collection?.editOrder ?? nextOrder,
    order: toNumber(order) ?? nextOrder,
  })

  // Reconcile this collection's visibility conditions against what's currently saved: create rows that are
  // new, update rows whose target option changed, and delete rows that were removed from the list. Callers
  // must validate every row is complete before calling this - an incomplete row here would either delete a
  // still-relevant saved condition or, if instead treated as "kept", leave a stale server-side condition
  // active that no longer matches what the UI showed when the drawer was saved.
  const applyConditions = async (collectionReferenceID: string) => {
    const keptDependencyRefs = new Set<string>()
    // Two rows targeting the same option would otherwise call createDependency/updateDependency twice for the
    // same target - de-duplicate by optionRef so only the first row with a given target is actually applied.
    const seenOptionRefs = new Set<string>()

    for (const condition of conditions) {
      if (!condition.optionRef || seenOptionRefs.has(condition.optionRef)) continue
      seenOptionRefs.add(condition.optionRef)
      if (condition.dependencyRef) keptDependencyRefs.add(condition.dependencyRef)

      if (condition.dependencyRef) {
        const original = currentConditions.find((entry) => entry.dependencyRef === condition.dependencyRef)
        if (original && original.optionRef !== condition.optionRef) {
          await updateDependency(collectionReferenceID, condition.dependencyRef, {
            dependencyFieldReferenceID: condition.optionRef,
            conditionalValue: null,
          })
        }
      } else {
        await createDependency(collectionReferenceID, {
          dependencyFieldReferenceID: condition.optionRef,
          conditionalValue: null,
        })
      }
    }

    const removed = currentConditions.filter((entry) => !keptDependencyRefs.has(entry.dependencyRef))
    for (const entry of removed) {
      await deleteDependency(collectionReferenceID, entry.dependencyRef)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!displayName.trim() || !itemKey.trim()) {
      setError('Enter a display name and an item key.')
      return
    }

    if (reservedGroupKeys.includes(itemKey.trim())) {
      setError(
        `The Item Key "${itemKey.trim()}" is already used by a field in this section. Choose a different Item Key.`,
      )
      return
    }

    // A condition row missing either half of "show only when X is set to Y" can't be resolved into a
    // dependency at all - block the save so the user fixes or removes it, rather than silently either
    // deleting the saved condition it used to represent or leaving a stale one active server-side.
    if (conditions.some((condition) => !condition.controllingFieldRef || !condition.optionRef)) {
      setError('Complete or remove every visibility condition before saving.')
      return
    }

    setSubmitting(true)
    try {
      if (collection) {
        await updateFormField(sectionReferenceID, collection.referenceID, buildCollectionPayload())
        await applyConditions(collection.referenceID)
      } else {
        const created = await createFormField(sectionReferenceID, buildCollectionPayload())
        const group = await createFormField(sectionReferenceID, {
          ...blankFieldPayload,
          editType: 'Group',
          editOrder: nextOrder,
          order: nextOrder,
        })
        await createDependency(group.referenceID, {
          dependencyFieldReferenceID: created.referenceID,
          conditionalValue: null,
        })
        await applyConditions(created.referenceID)
      }
      toast.success(collection ? 'Collection updated' : 'Collection created')
      onSaved()
      onOpenChange(false)
    } catch (submitError) {
      toast.error(
        `${submitError instanceof Error ? submitError.message : 'Failed to save the collection'} Some changes may already be applied. Please reopen and check before retrying.`,
      )
      // applyConditions runs several create/update/delete calls sequentially - if one of them (or an earlier
      // step) failed partway through, some may have already succeeded against the server while this drawer's
      // own conditions/currentConditions still describe the pre-submit state. Retrying from here would re-run
      // the same diff against that now-stale snapshot, re-creating already-created rows or re-deleting
      // already-removed ones. Refresh the canvas and close the drawer so the ONLY way to retry is reopening
      // it, which reloads the real current state instead of continuing to trust this stale one.
      onSaved()
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={submitting} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={submitting} disabled={submitting} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer
      isOpen={isOpen}
      title={collection ? 'Edit Collection' : 'Add Collection'}
      onClose={handleClose}
      footerItems={footer}
    >
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit}>
          <SectionHeading>Collection</SectionHeading>
          <FieldHelp>A repeatable group of fields — the agent can add multiple items (e.g. Pets, Insurance).</FieldHelp>
          <InputGroup
            label="Display Name *"
            placeholder="e.g. Pets"
            value={displayName}
            onChange={(event) => setDisplayName((event.target as HTMLInputElement).value)}
          />
          <div>
            <InputGroup
              label="Item key *"
              placeholder="e.g. pets"
              value={itemKey}
              onChange={(event) => setItemKey((event.target as HTMLInputElement).value)}
            />
            <FieldHelp>The array key each item is stored under. Its child fields all share this key.</FieldHelp>
          </div>
          <InputGroup
            label="Add button text"
            labelInformationTooltip="Label on the agent's add-another button (e.g. Add Pet)."
            placeholder="e.g. Add Pet"
            value={addButtonText}
            onChange={(event) => setAddButtonText((event.target as HTMLInputElement).value)}
          />
          <InputGroup
            label="Min items"
            inputMode="numeric"
            labelInformationTooltip="Fewest items the agent must add."
            value={min}
            onChange={(event) => setMin((event.target as HTMLInputElement).value)}
          />
          <InputGroup
            label="Max items"
            inputMode="numeric"
            labelInformationTooltip="Most items the agent can add."
            value={max}
            onChange={(event) => setMax((event.target as HTMLInputElement).value)}
          />
          <InputGroup
            label="Display Order"
            inputMode="numeric"
            labelInformationTooltip="Where this collection sits relative to the section's other fields in the read-only summary. Fields are shown lowest-to-highest; pre-existing (non-Admin-Builder) fields in this section may not have their own Order set, so a new collection can otherwise land earlier than intended."
            value={order}
            onChange={(event) => setOrder((event.target as HTMLInputElement).value)}
          />

          {conditionSources.length > 0 && (
            <>
              <SectionHeading>Visibility</SectionHeading>
              <FieldHelp>
                {conditions.length > 0
                  ? 'Shown only when ANY of these conditions match. Add another to show it for more than one trigger (e.g. both "Fixed" and "Periodic").'
                  : 'Optionally show this collection only when another field has a specific value.'}
              </FieldHelp>
              <OptionsBlock>
                {conditions.map((condition, index) => {
                  const controllingValue =
                    controllingOptions.find((option) => option.value === condition.controllingFieldRef) ?? null
                  const optionChoices = optionChoicesFor(condition.controllingFieldRef)
                  const optionChoiceValue = optionChoices.find((option) => option.value === condition.optionRef) ?? null
                  return (
                    <OptionRowLayout key={condition.key}>
                      <Select
                        label="Show only when"
                        options={controllingOptions}
                        value={controllingValue}
                        getOptionLabel={(option: EnumOption) => option.label}
                        getOptionValue={(option: EnumOption) => option.value}
                        onChange={(option: EnumOption | null) =>
                          updateConditionControllingField(index, option?.value ?? '')
                        }
                      />
                      <Select
                        label="is set to"
                        options={optionChoices}
                        value={optionChoiceValue}
                        disabled={!condition.controllingFieldRef}
                        getOptionLabel={(option: EnumOption) => option.label}
                        getOptionValue={(option: EnumOption) => option.value}
                        onChange={(option: EnumOption | null) => updateConditionOption(index, option?.value ?? '')}
                      />
                      <OptionRemoveWrap>
                        <Button
                          variant="tertiary"
                          useLinkStyle
                          size="small"
                          type="button"
                          onClick={() => removeCondition(index)}
                        >
                          Remove
                        </Button>
                      </OptionRemoveWrap>
                    </OptionRowLayout>
                  )
                })}
              </OptionsBlock>
              <div>
                <Button variant="secondary" size="small" iconLeft={<AddIcon />} type="button" onClick={addCondition}>
                  Add condition
                </Button>
              </div>
            </>
          )}

          {error && <FieldHelp style={{ color: 'var(--colour-text-danger, #d4285a)' }}>{error}</FieldHelp>}
        </FieldStack>
      )}
    </Drawer>
  )
}
